import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { zeosDialog } from '../../../components/Shared/confirm-dialog/ConfirmDialog';
import { ZeosApiContext } from '../../../context/ZeosApiContext';
import { PropertyForm } from '../../../forms/PropertyForm';
import { useFormSubmission } from '../../../hooks/useFormSubmission';
import { useFormValidation } from '../../../hooks/useFormValidation';
import {
    clearPropertyDraft, propertyDraftSelector, setPropertyDraft
} from '../../../redux-store/OnboardingSlice';
import { Analytics } from '../../../services/analytics/Analytics';
import { IZeosUser } from '../../../services/auth/IZeosUser';
import { defaultPropertyForm } from '../../../services/forms/DefaultFormData';
import { IPropertyFormDataType } from '../../../services/forms/FormDataTypes';
import {
    PropertyValidationSchema
} from '../../../services/forms/validationSchemas/PropertyValidationSchema';
import {
    mapPropertyFormToContract, uploadPropertyImages
} from '../../../services/helpers/PropertyHelpers';
import { createLogger } from '../../../services/Logger';
import { IListItemSimple } from '../../../services/zeos-api/contracts/ListUtility';
import {
    ZeosListEnum, ZeosListOptionEnum
} from '../../../services/zeos-api/enums/ListUtility';
import { REQUEST_ERROR } from '../../../services/zeos-api/ZeosApiExecutor';
import { OnboardContainer } from '../layout/OnboardContainer';

const log = createLogger("PropertyScreen");

export const PropertyScreen = () => {
  const api = useContext(ZeosApiContext);
  const dispatch = useDispatch();
  const propertyDraft = useSelector(propertyDraftSelector);

  const [companyId, setCompanyId] = useState<number>(0);
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [priorWorkTypeOptions, setPriorWorkTypeOptions] = useState<{ id: number; name: string }[]>([]);
  const [originalFormData, setOriginalFormData] =
    useState<IPropertyFormDataType | null>(null);
  const [zeosUser, setZeosUser] = useState<IZeosUser>();
  const [hvacOptions, setHvacOptions] = useState<IListItemSimple[]>([]);

  // Send user data back to OnboardTemplate
  const handleUserCallback = (user: IZeosUser) => {
    setZeosUser(user);
  };

  // Seed from an in-progress draft (survives back/forward nav) if present;
  // the initAsync effect below overwrites this with real backend data once
  // it resolves, so a draft can never mask an already-saved property.
  // `file` is never draft-persisted (File isn't serializable), so it always
  // starts empty here — the user re-attaches after navigating back.
  const { formData, setFormData, errors, formValidate, formChange } =
    useFormValidation<IPropertyFormDataType>(
      PropertyValidationSchema,
      propertyDraft ? { ...propertyDraft, file: [] } : defaultPropertyForm
    );

  // Persist form input to the draft as the user types, so it survives
  // navigating away and back. Skipped while rehydrating from the backend to
  // avoid overwriting a good draft with the transient pre-load state.
  useEffect(() => {
    if (!isLoadingData) {
      // ponytail: file uploads aren't draft-persisted (File isn't serializable); user re-attaches after nav.
      const { file, ...draft } = formData;
      dispatch(setPropertyDraft(draft));
    }
  }, [formData, isLoadingData, dispatch]);

  // Check if either utility substep is completed to determine if property form should be disabled
  const isUtilitySubstepCompleted = useMemo(() => {
    // No progress tracking - always return false
    return false;
  }, []);

  // Check if form data has been modified by user
  const checkFormDirty = useCallback(() => {
    if (!originalFormData) return false;
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  }, [formData, originalFormData]);

  // Update form dirty state when form data changes
  useEffect(() => {
    if (originalFormData) {
      const dirty = checkFormDirty();
      setIsFormDirty(dirty);
    }
  }, [formData, checkFormDirty, originalFormData]);

  useEffect(() => {
    const loadHvacOptions = async () => {
      try {
        const response = await api.ListUtility.getListAsync(
          ZeosListEnum.HvacPrimaryType,
          ZeosListOptionEnum.excludeUtility
        );
        setHvacOptions(response.data ?? []);
      } catch (error) {
        log.error("Failed to load HVAC system type options", {
          error: error instanceof Error ? error.message : error,
        });
      }
    };
    loadHvacOptions();
  }, []);

  useEffect(() => {
    const initAsync = async () => {
      // Wait for companyId; look up via the live API instead of cached auth
      // claims, which can be stale right after a company/property is created
      if (zeosUser && companyId) {
        setIsLoadingData(true);
        try {
          const response = await api.Property.propertiesByCompanyId(
            companyId
          );
          if (response.message === "error") {
            log.error("Failed to load properties for company", {
              error: response.message,
              companyId,
            });
            return;
          }
  
          const property = response.data?.[0];
          if (property) {
            const formData: IPropertyFormDataType = {
              acreage: property.acreage?.toString() ?? "",
              address: property.address,
              yearBuilt: property.yearBuilt?.toString() ?? "",
              bbl: property.bbl || undefined,
              bin: property.bin || undefined,
              buildingType: "",
              buildingTypeId: property.buildingTypeId,
              city: property.city,
              companyId: companyId,
              espmPropertyTypeCategoryId:
                property.espmPropertyTypeCategoryId ?? undefined,
              espmPropertyTypeDetailId:
                property.espmPropertyTypeDetailId ?? undefined,
              file: [],
              floors: property.floors ? property.floors.toString() : "",
              hasWifi: property.isWifiAvailable ? "Yes" : "No",
              hvacPrimaryTypeIds: property.hvacPrimaryTypeIds ?? [],
              land: property.landTypeId === 11 ? "Leased" : "Owned",
              mixedUseComponents: property.mixedUseComponents ?? undefined,
              paysBill: property.paysBill?.toString() ?? "",
              propertyId: property.propertyId,
              propertyName: property.propertyName,
              propertyTypeId: Number(property.propertyTypeId),
              roofSize: property.roofSize?.toString() ?? "",
              sqft: property.sqFt?.toString() ?? "",
              state: property.state,
              suite: property.suite,
              units: property.units?.toString() ?? "",
              utilityIds: property.meters
                ? Array.from(new Set(property.meters.map((m) => m.utilityId)))
                : [],
              electricUtilityProvider: property.electricUtilityProvider ?? "",
              gasUtilityProvider: property.gasUtilityProvider ?? "",
              priorWorkTypeIds: property.priorWorkTypeIds ?? [],
              zip: property.zip,
            };
            setFormData(formData);
            setOriginalFormData(formData);
            setIsFormDirty(false);
          }
          // No existing property yet — leave form blank for a genuine create
        } catch (error) {
          log.error("Exception while loading property data", {
            error: error instanceof Error ? error.message : error,
            companyId,
          });
        } finally {
          setIsLoadingData(false);
        }
      }
    };
    initAsync();
  }, [zeosUser?.zeosUserId, companyId]);

  useEffect(() => {
    const init = async () => {
      const customerId = zeosUser?.customerClaims[0]?.customerId;
      if (customerId) {
        const companies = await api.Company.companiesByCustIdAsync(customerId);
        const company = companies.data ? companies.data[0] : undefined;
        if (company !== undefined && typeof company.companyId === "number") {
          setCompanyId(company.companyId);
        }
      }
    };
    init();
  }, [zeosUser?.customerClaims[0]?.customerId]);

  // Fetch prior work type options once on mount
  useEffect(() => {
    const fetchPriorWorkTypes = async () => {
      try {
        const result = await api.ListUtility.getListAsync(ZeosListEnum.BuildingPriorWorkType);
        if (result.data && result.data.length > 0) {
          setPriorWorkTypeOptions(
            result.data.map((item) => ({ id: item.dataKey, name: item.display }))
          );
        }
      } catch (error) {
        log.error("Failed to load prior work type options", { error });
      }
    };
    fetchPriorWorkTypes();
  }, []);

  const submitPropertyData = async (
    formData: IPropertyFormDataType,
    mode: "put" | "post"
  ) => {
    const payload = mapPropertyFormToContract(formData, companyId);

    if (!import.meta.env.PROD) {
      // eslint-disable-next-line no-console
      console.debug("Submitting property payload", payload);
    }

    const result =
      mode === "put"
        ? await api.Property.putProperty(payload)
        : await api.Property.postProperty(payload);

    if (
      result?.zeosStatus?.toLowerCase() === "error" ||
      result?.message === REQUEST_ERROR
    ) {
      throw new Error(result?.message || "Property operation failed");
    }

    // Handle file uploads after successful property save
    const propertyId = result.data?.propertyId as number;
    if (propertyId && formData.file && formData.file.length > 0) {
      await uploadPropertyImages(api, propertyId, formData.file);
    }

    // Record the persisted ID so a repeat submit in this mount is a PUT,
    // not another POST (prevents duplicate creation on back/forward nav).
    if (mode === "post" && propertyId) {
      Analytics.capture("property_added", { source: "onboarding" });
      setFormData((prev) => ({ ...prev, propertyId }));
      setOriginalFormData((prev) =>
        prev ? { ...prev, propertyId } : prev
      );
    }

    dispatch(clearPropertyDraft());
  };

  // Setup form submission handler — decide create vs. update by whether a
  // persisted property already exists, not by navigation direction.
  const { handleSubmission } = useFormSubmission<IPropertyFormDataType>(
    Number(formData.propertyId) > 0,
    submitPropertyData
  );

  // Effectively the master controller for "Continue" button
  const handleNext = async (doNavigate: () => void) => {
    // Skip validation and submission if form is disabled (utility step already completed)
    if (isUtilitySubstepCompleted) {
      log.info(
        "Property form is disabled due to utility step completion, skipping validation and submission"
      );
      doNavigate();
      return;
    }

    // Skip validation and submission if data is loading and form is not dirty
    if (isLoadingData && !isFormDirty) {
      doNavigate();
      return;
    }

    // Guard against submitting before the company lookup has resolved --
    // otherwise this POSTs CompanyId: 0 and blows up on the FK constraint.
    if (!companyId) {
      log.error("Cannot submit property: companyId has not resolved yet");
      zeosDialog("Still loading your company information. Please wait a moment and try again.");
      return;
    }

    // Validate form data before submission
    const isValid = await formValidate(async () => {
      // This success callback will be called if validation passes
      try {
        // Submit form data
        await handleSubmission(formData, async () => {
          // Navigate without progress tracking
          doNavigate();
        });
      } catch (error) {
        // Error is already logged in handleSubmission
        console.error("Failed to complete property onboarding step:", error);
        zeosDialog("Failed to save property information. Please try again.");
      }
    });

    // If validation failed, isValid will be false and we return early
    if (!isValid) {
      return;
    }
  };

  const form = (
    <PropertyForm
      formData={formData}
      errors={errors}
      handleChange={formChange}
      setFormData={setFormData}
      priorWorkTypeOptions={priorWorkTypeOptions}
      disabled={isUtilitySubstepCompleted}
      hvacOptions={hvacOptions}
      showServingUtilitySelectors
    />
  );

  return (
    <OnboardContainer
      content={form}
      contentType="form"
      pageSlug={"property"}
      onNext={handleNext}
      userCallback={handleUserCallback}
      isFormDisabled={isUtilitySubstepCompleted}
      disabledMessage={
        "You have already completed the Utility Connection step. You can update your property and utility information in the Dashboard."
      }
    />
  );
};
